import type { TFunction } from 'i18next';

import type { KpiExclusionReason } from '@/domain/feedingDashboard';

import { formatDashboardDay } from './dashboardDay';

export interface ReasonContext {
    /** The configured planning-system name, already resolved. */
    system: string;
    /** The day closing-day values are read on, as the API sends it (YYYY-MM-DD). */
    closingDay: string | null;
    /** True for a value over the whole period (feed, pellet) rather than the closing day. */
    period: boolean;
    /**
     * True when the text printed right before the reason already names the planning
     * system ("Mercatus: …"), so an unmapped signal need not name it again.
     */
    systemNamed?: boolean;
}

// Full keys rather than assembled ones, so the i18n scanner can see every key in use.
const KEYS = {
    feedingServiceUnavailable: 'feedingModule.kpi.excluded.feedingServiceUnavailable',
    noBioscope: 'feedingModule.kpi.excluded.noBioscope',
    noBioscopeData: 'feedingModule.kpi.excluded.noBioscopeData',
    collecting: 'feedingModule.kpi.excluded.collecting',
    noFeedingCamera: 'feedingModule.kpi.excluded.noFeedingCamera',
} as const;

/**
 * The reason a pen has no value, in words. One wording for the KPI notes and the table's
 * cell titles, so a pen is never explained two ways on one page.
 */
export function exclusionReasonText(
    reason: KpiExclusionReason,
    t: TFunction,
    context: ReasonContext
): string {
    if (reason === 'notConfigured') {
        return context.systemNamed
            ? t('feedingModule.kpi.excluded.notConfiguredShort')
            : t('feedingModule.kpi.excluded.notConfigured', { system: context.system });
    }
    if (reason === 'noValue') {
        return context.period || context.closingDay === null
            ? t('feedingModule.kpi.excluded.noValuePeriod')
            : t('feedingModule.kpi.excluded.noValue', {
                  day: formatDashboardDay(context.closingDay),
              });
    }
    return t(KEYS[reason]);
}
